import { Directive, inject, OnDestroy, OnInit } from '@angular/core';

import { Observable, Subscription } from 'rxjs';
import { UnsavedChangesBase } from './unsaved-changes-base';
import { Policy } from '../../../shared/models/info/policy';
import { PaginationService } from '../../services/pagination.service';
import { AccessRightsService } from '../../services/access-rights.service';
import { Pagination } from '../../../shared/models/internal/pagination';
import { SwPartNumberService } from '../../services/sw-part-number.service';
import { STORAGE_KEYS } from '../../../shared/constants/storage-keys';
import { SwPartNumberState } from '../../../shared/enums/sw-part-number-state';
import { SelectedTableElement } from '../../../shared/models/internal/selected-table-element';
import { HotToastService } from '@ngxpert/hot-toast';

@Directive()
export abstract class OverviewBaseComponent<T> extends UnsavedChangesBase implements OnInit, OnDestroy {
  public viewHistoryDisabled = true;
  public policy = Policy;
  public sidebarOpen = false;
  public sideBarTitle = '';

  public items?: T[] = [];
  public itemsTotalCount = 0;
  public swPartNumberIsReadOnly = false;

  public sidebarWidth = 400;

  protected swPartNumberService = inject(SwPartNumberService);
  protected paginationService = inject(PaginationService<T>);
  protected accessRightsService = inject(AccessRightsService);
  protected toastr = inject(HotToastService);

  protected isResizing = false;
  protected minWidth = Math.round(window.innerWidth * 0.3);
  protected maxWidth = Math.round(window.innerWidth * 0.6);

  // TODO: Keep this in the future with a generic entity and remove other entityDataId, selectedEntity etc.
  protected selectedTableElement: SelectedTableElement = new SelectedTableElement();

  private subscriptions = new Subscription();

  constructor(key: string) {
    super(key);
    document.addEventListener('mousemove', this.onMouseMove.bind(this));
    document.addEventListener('mouseup', this.stopResize.bind(this));
    window.addEventListener('resize', this.onWindowResize.bind(this));
  }

  public override ngOnInit(): void {
    super.ngOnInit();
    this.accessRightsService.getAccessRights$().subscribe((ar) => {
      this.viewHistoryDisabled = !ar.authorized(Policy.canReadHistory);
    });

    this.paginationService.initialize();
    this.initSidebarWidth();

    this.subscriptions.add(
      this.paginationService.items$.subscribe((items) => {
        this.items = items;
      })
    );

    this.subscriptions.add(
      this.paginationService.totalItems$.subscribe((totalCount) => {
        this.itemsTotalCount = totalCount;
      })
    );
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    document.removeEventListener('mousemove', this.onMouseMove.bind(this));
    document.removeEventListener('mouseup', this.stopResize.bind(this));
    window.removeEventListener('resize', this.onWindowResize.bind(this));
    this.subscriptions.unsubscribe();
  }

  protected async fetchPagedData(
    resetPagination: boolean,
    pageFetchFn: (page: number, pageSize: number) => Observable<Pagination<T>>
  ): Promise<void> {
    if (resetPagination) {
      this.paginationService.initialize();
    }

    await this.paginationService.loadNextPage(pageFetchFn);
    this.evaluateSwPartNumberState();
  }

  protected async fetchPagedDataUntilPage(
    targetPage: number,
    pageFetchFn: (page: number, pageSize: number) => Observable<Pagination<T>>
  ): Promise<void> {
    this.paginationService.reset();
    while (this.paginationService.currentPageNumber < targetPage && this.paginationService.hasMoreData$) {
      await this.paginationService.loadNextPage(pageFetchFn);
    }
    this.evaluateSwPartNumberState();
  }

  protected evaluateSwPartNumberState(): void {
    const stateToCheck = this.swPartNumberService.getSwPartNumber()?.state;

    if (!stateToCheck) {
      return;
    }

    this.swPartNumberIsReadOnly = stateToCheck != SwPartNumberState.Dev;
  }

  protected onMouseMove(event: MouseEvent): void {
    if (!this.isResizing) return;

    const newWidth = window.innerWidth - event.clientX;

    if (newWidth >= this.minWidth && newWidth <= this.maxWidth) {
      this.sidebarWidth = newWidth;
    }
  }

  protected stopResize(): void {
    if (this.isResizing) {
      this.isResizing = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';

      localStorage.setItem(STORAGE_KEYS.SIDEBAR_WIDTH, this.sidebarWidth.toString());
    }
  }

  protected onWindowResize(): void {
    this.maxWidth = Math.round(window.innerWidth * 0.6);
    this.minWidth = Math.round(window.innerWidth * 0.3);

    if (this.sidebarWidth > this.maxWidth) {
      this.sidebarWidth = this.maxWidth;
    } else if (this.sidebarWidth < this.minWidth) {
      this.sidebarWidth = this.minWidth;
    }
  }

  protected initSidebarWidth(): void {
    this.sidebarWidth = parseInt(localStorage.getItem(STORAGE_KEYS.SIDEBAR_WIDTH) || '400', 10);
    if (this.sidebarWidth > this.maxWidth) {
      this.sidebarWidth = this.maxWidth;
    }
  }
}
